import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Star, ShieldCheck, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function BrowsePage() {
  const [params, setParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [pros, setPros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const cat = params.get("category") || "";

  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)); }, []);

  useEffect(() => {
    setLoading(true);
    const query = {};
    if (cat) query.category = cat;
    if (q) query.q = q;
    api.get("/professionals", { params: query }).then((r) => {
      setPros(r.data); setLoading(false);
    });
  }, [cat, q]);

  const setCat = (v) => {
    const next = new URLSearchParams(params);
    if (v === "all") next.delete("category"); else next.set("category", v);
    setParams(next);
  };

  return (
    <div className="bg-[#05070C] min-h-[80vh]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="text-center mb-12">
          <div className="label-eyebrow">Directory</div>
          <h1 className="font-display text-4xl md:text-6xl mt-4">Meet the <span className="serif-italic text-[#2DD4BF]">experts</span></h1>
          <div className="w-16 h-[2px] bg-[#2DD4BF] mx-auto mt-6" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-10 max-w-4xl mx-auto">
          <div className="md:col-span-8">
            <Input data-testid="browse-search" placeholder="Search by skill, name, or headline…"
                   value={q} onChange={(e) => setQ(e.target.value)}
                   className="h-12 bg-[#0F1B33] border-[#142542] text-[#E8ECF3]" />
          </div>
          <div className="md:col-span-4">
            <Select value={cat || "all"} onValueChange={setCat}>
              <SelectTrigger className="h-12 bg-[#0F1B33] border-[#142542] text-[#E8ECF3]" data-testid="browse-category-select">
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent className="bg-[#0F1B33] border-[#142542] text-[#E8ECF3]">
                <SelectItem value="all">All categories</SelectItem>
                {categories.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="text-[#94A3B8] text-center py-16">Loading experts…</div>
        ) : pros.length === 0 ? (
          <div className="border border-dashed border-[#142542] rounded-3xl p-16 text-center text-[#94A3B8]">
            No experts match those filters yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pros.map((p) => (
              <Link to={`/professionals/${p.user_id}`} key={p.user_id}
                    className="group section-card hover-lift"
                    data-testid={`pro-card-${p.user_id}`}>
                <div className="flex items-start gap-4">
                  <img src={p.picture || "https://images.unsplash.com/photo-1607746882042-944635dfe10e?crop=entropy&cs=srgb&fm=jpg&h=200&w=200"} alt=""
                       className="w-16 h-16 rounded-full object-cover border border-[#142542]" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-display text-lg text-[#E8ECF3]">{p.name}</div>
                      {p.verified ? (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-[#2DD4BF]/20 border border-[#2DD4BF]/40 text-[#2DD4BF] rounded-full px-2 py-0.5">
                          <ShieldCheck className="h-3 w-3" /> Verified
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-[#142542] text-[#94A3B8] rounded-full px-2 py-0.5">
                          Pending
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#94A3B8] mt-1">{p.headline}</div>
                  </div>
                </div>
                <div className="mt-4 text-sm text-[#CBD5E1] line-clamp-2">{p.bio}</div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {(p.expertise || []).slice(0, 3).map((e) => (
                    <span key={e} className="text-xs bg-[#0A1223] border border-[#142542] text-[#94A3B8] rounded-full px-3 py-1">{e}</span>
                  ))}
                </div>
                <div className="mt-5 flex items-center justify-between text-sm">
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1 text-[#E8ECF3]">
                      <Star className="h-4 w-4 fill-[#2DD4BF] text-[#2DD4BF]" /> {p.avg_rating || "—"}
                    </span>
                    <span className="flex items-center gap-1 text-[#94A3B8]">
                      <MapPin className="h-3 w-3" /> {p.location?.split(",")[0]}
                    </span>
                  </div>
                  <div className="font-display text-lg text-[#2DD4BF]">₹{p.hourly_rate}<span className="text-xs text-[#94A3B8]">/hr</span></div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
